import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../components/common/Button';
import { userService, authService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`max-width: 900px; margin: 0 auto;`;
const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
`;
const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Title = styled.h1`font-size: 22px; font-weight: 700; color: #111827; margin: 0;`;
const UserRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  border-bottom: 1px solid #f9fafb;
  gap: 12px;
  flex-wrap: wrap;
  &:last-child { border-bottom: none; }
`;
const UserInfo = styled.div`display: flex; align-items: center; gap: 12px;`;
const Avatar = styled.div<{ role: string }>`
  width: 40px; height: 40px; border-radius: 50%;
  background: ${p => p.role === 'admin' ? '#ede9fe' : p.role === 'cook' ? '#fef3c7' : '#eff6ff'};
  display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
`;
const UserName = styled.div`font-size: 15px; font-weight: 600; color: #111827;`;
const UserMeta = styled.div`font-size: 12px; color: #6b7280;`;
const Badge = styled.span<{ color: string }>`
  font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 99px;
  background: ${p => p.color}22; color: ${p => p.color};
`;
const Actions = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 100;
`;
const ModalBox = styled.div`
  background: white; border-radius: 12px; padding: 24px;
  width: 100%; max-width: 420px; box-shadow: 0 4px 24px rgba(0,0,0,0.12);
`;
const ModalTitle = styled.h2`font-size: 18px; font-weight: 700; color: #111827; margin: 0 0 16px;`;
const Label = styled.label`font-size: 13px; font-weight: 500; color: #374151; display: block; margin-bottom: 4px;`;
const Input = styled.input`
  width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 14px; box-sizing: border-box; margin-bottom: 12px;
  &:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
`;
const ErrorMsg = styled.div`color: #991b1b; font-size: 13px; margin-bottom: 8px;`;
const SuccessMsg = styled.div`color: #065f46; background: #d1fae5; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px;`;
const ModalActions = styled.div`display: flex; gap: 10px; justify-content: flex-end; margin-top: 4px;`;

interface User {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'cook' | 'waiter';
  is_active: boolean | number;
  last_login?: string;
}

const roleLabel = (r: string) => r === 'admin' ? 'Admin' : r === 'cook' ? 'Cuoco' : 'Cameriere';
const roleIcon = (r: string) => r === 'admin' ? '🔑' : r === 'cook' ? '👨‍🍳' : '🧑‍💼';
const roleColor = (r: string) => r === 'admin' ? '#7c3aed' : r === 'cook' ? '#d97706' : '#2563eb';

const formatDate = (iso?: string) => {
  if (!iso) return 'Mai';
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
    ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

const Users: React.FC = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [passwordModal, setPasswordModal] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    try {
      const res = await userService.list();
      setUsers(res.data?.users || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const canChangePassword = (target: User) => {
    if (target.role !== 'admin') return true;
    return target.id === me?.id;
  };

  const openPasswordModal = (u: User) => {
    setPasswordModal(u);
    setNewPassword('');
    setConfirmPassword('');
    setModalError('');
    setModalSuccess('');
  };

  const handleChangePassword = async () => {
    if (!passwordModal) return;
    if (newPassword.length < 6) { setModalError('La password deve essere di almeno 6 caratteri'); return; }
    if (newPassword !== confirmPassword) { setModalError('Le password non coincidono'); return; }
    setSaving(true);
    setModalError('');
    try {
      await userService.updatePassword(passwordModal.id, newPassword);
      setModalSuccess('Password aggiornata con successo');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordModal(null), 1500);
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u: User) => {
    const newState = !u.is_active;
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_active: newState } : x));
    try {
      await userService.setActive(u.id, newState);
    } catch {
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_active: u.is_active } : x));
    }
  };

  if (me?.role !== 'admin') {
    return <Container><Card><CardHeader><Title>Accesso negato</Title></CardHeader></Card></Container>;
  }

  return (
    <Container>
      <Card>
        <CardHeader>
          <Title>Gestione Utenti</Title>
        </CardHeader>

        {loading ? (
          <div style={{ padding: '24px', color: '#6b7280' }}>Caricamento...</div>
        ) : (
          users.map(u => (
            <UserRow key={u.id}>
              <UserInfo>
                <Avatar role={u.role}>{roleIcon(u.role)}</Avatar>
                <div>
                  <UserName>{u.username} {u.id === me?.id && <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '12px' }}>(tu)</span>}</UserName>
                  <UserMeta>
                    <Badge color={roleColor(u.role)}>{roleLabel(u.role)}</Badge>
                    {' · '}
                    <Badge color={u.is_active ? '#10b981' : '#ef4444'}>{u.is_active ? 'Attivo' : 'Disattivato'}</Badge>
                    {' · Ultimo accesso: '}{formatDate(u.last_login)}
                  </UserMeta>
                </div>
              </UserInfo>
              <Actions>
                {canChangePassword(u) && (
                  <Button size="small" variant="secondary" onClick={() => openPasswordModal(u)}>
                    🔑 Password
                  </Button>
                )}
                {u.id !== me?.id && u.role !== 'admin' && (
                  <Button
                    size="small"
                    variant={u.is_active ? 'danger' : 'secondary'}
                    onClick={() => handleToggleActive(u)}
                  >
                    {u.is_active ? 'Disattiva' : 'Attiva'}
                  </Button>
                )}
              </Actions>
            </UserRow>
          ))
        )}
      </Card>

      {passwordModal && (
        <Modal onClick={() => setPasswordModal(null)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Cambia password — {passwordModal.username}</ModalTitle>
            {modalSuccess && <SuccessMsg>{modalSuccess}</SuccessMsg>}
            {modalError && <ErrorMsg>{modalError}</ErrorMsg>}
            <Label>Nuova password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setModalError(''); }}
              placeholder="Minimo 6 caratteri"
              autoFocus
            />
            <Label>Conferma password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setModalError(''); }}
              placeholder="Ripeti la password"
            />
            <ModalActions>
              <Button variant="secondary" onClick={() => setPasswordModal(null)}>Annulla</Button>
              <Button variant="primary" onClick={handleChangePassword} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </ModalActions>
          </ModalBox>
        </Modal>
      )}
    </Container>
  );
};

export default Users;
